import React, { useCallback, useContext, useMemo, useState } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import styled from 'styled-components'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextButton from 'components/Luxkit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { rem } from 'polished'
import moment from 'moment'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { SEARCH_VERTICALS } from 'constants/search'
import { useAppDispatch } from 'hooks/reduxHooks'
import { CruiseDurationOptions } from './Inputs/CruiseWhenSelect/CruiseWhenSelectHowLong'
import TextLink from 'components/Luxkit/TextLink'
import Group from 'components/utils/Group'
import LineFilterAltIcon from 'components/Luxkit/Icons/line/LineFilterAltIcon'
import ModalContext from 'contexts/ModalContext'
import CruiseSearchFiltersDrawer from './CruiseSearchExtraFiltersDrawer'
import { deleteSearchParams, setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { CruiseSearchFilterKeys } from 'constants/cruise'
import { replace } from 'connected-react-router'
import { useLocation } from 'react-router'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { take } from 'lib/array/arrayUtils'
import useSlimMobileSearch from 'components/SearchV2/Hooks/useSlimMobileSearch'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import CruiseSearchChips, {
  HeaderContainer,
} from 'components/Cruises/SearchPage/Filters/Inputs/CruiseSearchMobileChips/CruiseSearchChips'
import { CruiseSearchSortOptions } from 'components/Cruises/SearchPage/Filters/Inputs/CruiseSortDropdown'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import Carousel from 'components/Luxkit/Carousel/Carousel'
import useToggle from 'hooks/useToggle'
import Clickable from 'components/Common/Clickable'
import zIndex from 'styles/tools/z-index'
import cn from 'clsx'
import { SearchMenuStates } from 'components/Search/type'
import useScrollDirection from 'hooks/useScrollDirection'
import { useInView } from 'react-intersection-observer'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'

const SectionContainer = styled(LayoutContainer)`
   background-color: ${props => props.theme.palette.neutral.default.eight};
   position: sticky;
   top: 0;
   margin-top: 0;
   transition: top 1s ease-in-out;
   z-index: ${zIndex.searchMenu};

  &.offScreen {
    width: 100%;
    top: -50%;
    padding: ${rem(12)};
    box-shadow: ${props => props.theme.shadow.bottom.small};
    transition: top 1s ease-in-out;
  }
  &.lifted {
    top: 0;
    padding: ${rem(12)};
    transition: top 0.5s ease-out;
    z-index: ${zIndex.stickySubHeader};
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const SearchContainer = styled(VerticalSpacer)`
  padding: ${rem(20)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} 0;
  }
`

const EditGroup = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  padding: ${rem(8)};
  width: 100%;
`

interface Props {
  filters: App.OfferListFilters;
  className?: string;
}

function getDestinationName(
  locations: App.CruiseSearchFacet[],
  locationId?: string,
  locationIds?: string[],
) {
  const selectedItems = locations.filter(({ value }) => (
    value === locationId ||
    locationIds?.includes(value || '')
  )).map(({ name }) => name.split(',')[0])

  if (selectedItems.length > 1) {
    return `${selectedItems[0]} +${selectedItems.length - 1}`
  }

  return selectedItems[0]
}

const IGNORED_FILTERS = new Set(['sortBy', 'priceBy', 'minPrice', 'maxPrice'])

function CruiseSearchFiltersMobile(props: Props) {
  const { filters } = props
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const openCruisesSearch = useCallback(() => { toggleSearchVertical(SEARCH_VERTICALS.CRUISES) }, [toggleSearchVertical])
  const [cruiseLines] = useCruiseSearchFacets({ facetTypes: ['cruise_lines'] })
  const [destinations] = useCruiseSearchFacets({ facetTypes: ['destination_ports'] })
  const [departures] = useCruiseSearchFacets({ facetTypes: ['departure_ports'] })
  const showSlimMobileSearch = useSlimMobileSearch()
  const [isShowChips, toggleIsShowChips, showIsShowChips, hideIsShowChips] = useToggle(true)
  const [countSelected, setCountSelected] = useState(0)

  const {
    departureMonths: flexibleMonths,
    durationRange,
  } = filters
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const { search } = useLocation()

  const firstLineFilters = useMemo(() => {
    const destinationName = getDestinationName(destinations, filters.destinationId, filters.destinationIds)
    const departureName = getDestinationName(departures, filters.departurePlaceId, filters.departureIds)
    const data : string[] = []

    data.push(departureName ? `Starts in ${departureName}` : 'All departure ports')
    data.push(destinationName ? `Cruising to ${destinationName}` : 'All destinations')

    return data.join('. ')
  }, [filters.destinationId, filters.destinationIds, filters.departurePlaceId, filters.departureIds, destinations, departures])

  const secondLineFilters = useMemo(() => {
    const data: string[] = []

    const allCruiseLineNames = cruiseLines?.filter(cruiseLine => filters.cruiseLines?.includes(cruiseLine.value ?? ''))
      .map(cruiseLine => cruiseLine.name)

    if (allCruiseLineNames.length > 0) {
      const [firstName] = allCruiseLineNames
      const more = allCruiseLineNames.length > 1 ? `+${allCruiseLineNames.length - 1}` : ''
      let cruiseLineNames = firstName && `${firstName}`

      if (more) {
        cruiseLineNames = `${cruiseLineNames} ${more}`
      }

      data.push(cruiseLineNames)
    } else {
      data.push('All cruise lines')
    }

    const durations = CruiseDurationOptions.filter(opt => durationRange?.includes(opt.duration))
    const valueDuration = durations?.map(value => value.name).join(', ')
    data.push(valueDuration || 'Any duration')

    const departureMonths = flexibleMonths?.filter(Boolean) ?? []
    if (departureMonths.length) {
      const limitVisible = 1
      const formattedMonths = departureMonths?.map(month => moment(month, 'YYYY-MM').format('MMM YYYY'))
      const more = formattedMonths.length > limitVisible ? `+${formattedMonths.length - limitVisible}` : ''
      const dates = take(formattedMonths, limitVisible)

      const dateValues = more ? `${dates.join(', ')} ${more}` : dates.join(', ')

      if (dateValues) {
        data.push(dateValues)
      }
    }

    return data.join('. ')
  }, [cruiseLines, flexibleMonths, filters.cruiseLines, durationRange])

  const facetParams = useMemo(() => ({
    facetTypes: ['ship_names', 'cabin_types', 'special_offers', 'lux_exclusive', 'cruise_prices'],
    cruiseLines: filters.cruiseLines,
    departurePlaceId: filters.departurePlaceId,
    departureIds: filters.departureIds,
    rateCodes: filters.rateCodes,
    destinationId: filters.destinationId,
    destinationIds: filters.destinationIds,
    departureMonths: filters.departureMonths,
    durationMin: filters.durationMin,
    durationMax: filters.durationMax,
    departureStartDate: filters.departureStartDate,
    departureEndDate: filters.departureEndDate,
  } as unknown as App.CruiseSearchFacetParams), [filters])

  const [facets] = useCruiseSearchFacets(facetParams)

  const countSelectedFilters = useCallback((newFilters: Record<string, any>) => {
    const countSeleted = Object.keys(newFilters).reduce((acc, key) => {
      if (IGNORED_FILTERS.has(key) || !newFilters[key]) return acc

      const value = typeof newFilters[key] === 'object' ? newFilters[key] : [newFilters[key]]
      return acc + value.length
    }, 0)
    setCountSelected(countSeleted)
  }, [])

  const onShowFilters = useCallback(async() => {
    const newFilters = await showModal<Record<string, any>>(
      <CruiseSearchFiltersDrawer listFilters={filters} onClose={toggleIsShowChips} countSelected={countSelected}/>,
    )

    if (newFilters && Object.getOwnPropertyNames(newFilters).length > 0) {
      const priceFacet = facets.find(facet => facet.category === 'Price')
      if (newFilters.price?.start) {
        newFilters.minPrice = newFilters.price?.start
      }

      if (newFilters.price?.end && newFilters.price?.end !== priceFacet?.max) {
        newFilters.maxPrice = newFilters.price?.end
      }
      const newFiltersMapped = {
        ...newFilters,
        price: undefined,
      }

      countSelectedFilters(newFiltersMapped)

      const urlValues = Object.entries(newFiltersMapped)
        .filter(([_, value]) => !!value)
        .map(([key, value]) => ({ paramName: key, paramValue: value }))
      // reset all our possible filter keys
      let nextSearch = deleteSearchParams(search, 'sortBy', ...CruiseSearchFilterKeys)
      // then take whatever is now applicable
      nextSearch = setManySearchParamValues(nextSearch, urlValues)
      dispatch(replace({ search: nextSearch.toString() }))
    } else {
      setCountSelected(0)
      dispatch(replace({ search: undefined }))
    }
  }, [showModal, filters, toggleIsShowChips, countSelected, facets, countSelectedFilters, search, dispatch])

  const {
    activeMenu,
  } = useContext(GlobalSearchStateContext)
  const scrollDirection = useScrollDirection({ tolerance: 20 })
  const [toLiftRef, liftInView] = useInView()
  const isLifted = !liftInView && scrollDirection === 'up'
  const isOffScreen = !liftInView && scrollDirection !== 'up'

  return <>
    <SectionContainer
      className={cn({
        lifted: isLifted || activeMenu !== SearchMenuStates.Closed,
        offScreen: isOffScreen && activeMenu === SearchMenuStates.Closed,
      })}
    >
      {showSlimMobileSearch && <Group direction="vertical" gap={12}>
        <Clickable onClick={openCruisesSearch}>
          <EditGroup>
            <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between">
              <Group direction="vertical" verticalAlign="center">
                <Heading variant="heading6">{firstLineFilters}</Heading>
                <Caption variant="large">
                  {secondLineFilters}
                </Caption>
              </Group>
              <LineSearchIcon size={20}/>
            </Group>
          </EditGroup>
        </Clickable>
        <HeaderContainer>
          <Carousel gap={8} gutterStyle="overflow">
            <FilterChip
              selected={countSelected > 0}
              size="medium"
              startIcon={<LineFilterAltIcon/>}
              onClick={async() => {
                hideIsShowChips()
                await onShowFilters()
                showIsShowChips()
              }}
            >
              All filters {countSelected > 0 && `(${countSelected})`}
            </FilterChip>
            {isShowChips && <CruiseSearchChips
              onShowFilters={onShowFilters}
              listFilters={filters}
              sortOptions={CruiseSearchSortOptions}
            />}
          </Carousel>
        </HeaderContainer>
      </Group>}
      {!showSlimMobileSearch && <SearchContainer gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between" gap={12} verticalAlign="center">
          <VerticalSpacer gap={4}>
            <BodyText variant="medium" weight="bold">
              {firstLineFilters}
            </BodyText>
            <BodyText variant="medium" colour="neutral-two">
              {secondLineFilters}
            </BodyText>
          </VerticalSpacer>
          <TextLink size="medium" onClick={openCruisesSearch}>Edit</TextLink>
        </Group>
        <TextButton
          kind="secondary"
          variant="dark"
          startIcon={<LineFilterAltIcon/>}
          fit="full-width"
          onClick={onShowFilters}
          size="small"
        >
          Sort & filter
        </TextButton>
      </SearchContainer>}
    </SectionContainer>
    <div ref={toLiftRef}/>
  </>
}

export default CruiseSearchFiltersMobile
